import { useEffect, useRef, useState } from 'react';
import { config } from '../../config';
import type { WorkflowEntry } from '../../app/coach';
import type { AdjustmentEntry, HypothesisPhase } from '../../types';
import { PHASE_OPTIONS } from '../../ui/halftime/phaseOptions';
import { PhaseIcon } from '../PhaseIcon';
import type { AdjustmentDraftB } from './useWorkflowB';

/**
 * B-15 — the **adjustment editor** (design spec §2.5 step 3): the one sheet behind *Edit* and
 * *Add my own*. Its Save button **is** the commit — §2.5's only concession to buffered text, and
 * the reason a kill costs at most this sheet's unsaved characters.
 *
 * ── Non-modal, unlike B-6's `SheetB` ────────────────────────────────────────
 *
 * §2.5: *"the second-half whistle is never blocked by the workflow"*. `showModal()` inerts
 * everything outside the dialog, the transition control included, so this sheet takes the B-12
 * set-piece shape instead — `show()`, positioned over the middle band, chrome live around it.
 * The explicit Cancel is the one close path (a non-modal dialog fires no `cancel` event).
 *
 * ── Editing an AI suggestion starts from Coach Claude's words ───────────────
 *
 * Requirement 26: *"accepted text starts as Coach Claude's and is tweakable"*. The field is
 * seeded from the entry's current text; `editEntryB` is what retains the original on the first
 * edit, so this component never has to know whether it is the first.
 */

export interface AdjustmentSheetBProps {
  /** `null` = *Add my own*; an entry = *Edit*. */
  readonly entry: AdjustmentEntry | null;
  /** The Step-1 selection, in DISC-4 order — the only patterns an adjustment may cite. */
  readonly selectable: readonly WorkflowEntry[];
  readonly onSave: (draft: AdjustmentDraftB) => void;
  readonly onClose: () => void;
}

export function AdjustmentSheetB({ entry, selectable, onSave, onClose }: AdjustmentSheetBProps) {
  const dialog = useRef<HTMLDialogElement | null>(null);
  const [text, setText] = useState(entry?.text ?? '');
  const [phase, setPhase] = useState<HypothesisPhase | null>(entry?.phase ?? null);
  const [sources, setSources] = useState<readonly string[]>(entry?.sourceHypothesisIds ?? []);

  useEffect(() => {
    const element = dialog.current;
    if (element === null || element.open) return;
    element.show();
  }, []);

  const trimmed = text.trim();
  const tooLong = trimmed.length > config.COACH_ADJUSTMENT_MAX_CHARS;
  // Phase is required with NO default (the B-7 convention): a default would file every
  // freehand adjustment on one phase, which is precisely the fact the card exists to carry.
  const ready = trimmed !== '' && !tooLong && phase !== null;

  return (
    <dialog ref={dialog} className="adjs" aria-labelledby="adjs-title" data-testid="adjs">
      <header className="adjs-bar">
        <h2 className="adjs-title" id="adjs-title">
          {entry === null ? 'Write an adjustment' : 'Edit the adjustment'}
        </h2>
        <div className="adjs-bar__actions">
          <button type="button" className="adjs-secondary" data-testid="adjs-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="adjs-primary"
            data-testid="adjs-save"
            disabled={!ready}
            onClick={() => {
              if (phase === null) return;
              onSave({ text: trimmed, phase, sourceHypothesisIds: sources });
            }}
          >
            Save
          </button>
        </div>
      </header>

      <div className="adjs-body">
        <section className="adjs-block">
          <label className="adjs-label" htmlFor="adjs-text">
            The adjustment, as you will say it
          </label>
          <textarea
            id="adjs-text"
            className="adjs-field"
            data-testid="adjs-text"
            rows={4}
            value={text}
            placeholder="Wings make immediate touchline runs on regain; play long in behind them."
            onChange={(event) => {
              setText(event.target.value);
            }}
          />
          {tooLong && (
            <p className="adjs-warn" role="status" data-testid="adjs-too-long">
              That is longer than {config.COACH_ADJUSTMENT_MAX_CHARS} characters — a card page has
              to read at arm’s length.
            </p>
          )}
        </section>

        <section className="adjs-block">
          <p className="adjs-label">Which phase does it apply to?</p>
          <div className="adjs-phases" role="group" aria-label="Game phase">
            {PHASE_OPTIONS.map((option) => (
              <button
                key={option.phase}
                type="button"
                className="adjs-phase"
                aria-pressed={phase === option.phase}
                data-testid={`adjs-phase-${option.phase}`}
                onClick={() => {
                  setPhase(option.phase);
                }}
              >
                <PhaseIcon phase={option.phase} />
                <span>{option.label}</span>
              </button>
            ))}
          </div>
        </section>

        {selectable.length > 0 && (
          <section className="adjs-block">
            <p className="adjs-label">What did you see that led to it? (optional)</p>
            <ul className="adjs-sources" data-testid="adjs-sources">
              {selectable.map((option) => {
                const on = sources.includes(option.hypothesis.id);
                return (
                  <li key={option.hypothesis.id}>
                    <button
                      type="button"
                      className="adjs-source"
                      aria-pressed={on}
                      data-testid={`adjs-source-${option.hypothesis.id}`}
                      onClick={() => {
                        setSources((current) =>
                          on
                            ? current.filter((id) => id !== option.hypothesis.id)
                            : [...current, option.hypothesis.id],
                        );
                      }}
                    >
                      <span className="adjs-source__count">{option.hypothesis.cachedCount}</span>
                      <span className="adjs-source__text">{option.hypothesis.text}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>
    </dialog>
  );
}
